import type { EndorsementType } from "@/types";

// ─────────────────────────────────────────────────────────────
// Field schema types
// ─────────────────────────────────────────────────────────────

export type FieldType = "boolean" | "number" | "text" | "percent" | "dollar" | "select" | "date";

export interface FieldSchema {
  /** The config key (e.g., "is_requires_approved_vendor") */
  source: string;
  /** Human-readable label */
  label: string;
  /** Input type to render */
  type: FieldType;
  /** Default value for create mode */
  defaultValue: unknown;
  /** If set, this field is only shown when the named boolean field is truthy */
  showWhen?: string;
  /** For select fields: choices array */
  choices?: { id: string; name: string }[];
  /** Helper text shown below the input */
  helperText?: string;
  /** Whether this field is required */
  required?: boolean;
  /** Minimum value for numeric fields */
  min?: number;
  /**
   * Maximum value for numeric fields. `undefined` falls through to the type-based
   * default (PERCENT_MAX / DOLLAR_MAX / INTEGER_MAX); `null` disables the cap.
   */
  max?: number | null;
}

export type EndorsementFieldSchemaMap = Partial<Record<EndorsementType, FieldSchema[]>>;

// ─────────────────────────────────────────────────────────────
// Field schemas — aligned to ERD (ADR-017)
//
// Junction-table fields (tiers, entries, codes, phases, etc.)
// are NOT represented here — they render as sub-collection
// sections in EndorsementDetailPanel, not as config form fields.
// ─────────────────────────────────────────────────────────────

export const ENDORSEMENT_FIELD_SCHEMAS: EndorsementFieldSchemaMap = {
  deductible_modification: [
    { source: "is_requires_approved_vendor", label: "Requires Approved Vendor", type: "boolean", defaultValue: false },
    { source: "is_excludes_lasered", label: "Excludes Lasered Claimants", type: "boolean", defaultValue: false },
    { source: "specialty_drug_name", label: "Specialty Drug Name", type: "text", defaultValue: "" },
    {
      source: "modification_pct",
      label: "Modification Percentage",
      type: "percent",
      defaultValue: 0,
      helperText: "Percentage of deductible reduction applied",
    },
  ],

  family_deductible: [
    { source: "amount", label: "Family Deductible Amount", type: "dollar", defaultValue: 0, required: true },
    { source: "individual_limit", label: "Individual Limit", type: "dollar", defaultValue: 0 },
    { source: "max_members_counted", label: "Max Members Counted", type: "number", defaultValue: 0 },
  ],

  tiered_deductible: [], // Tiers managed via junction sub-collection

  aggregate_accommodation: [
    // AB#4643: Optional flat-dollar override for the annual attachment point.
    // Positioned first because the annual attachment is the highest-value
    // contract term on the endorsement — it determines every funding number
    // downstream. The accommodation_factor immediately below is the
    // formula-path counterpart.
    //
    // Out of scope for this PR (deferred to a follow-up): a consolidated
    // section that surfaces the read-only mirrors of covered_composite +
    // monthly_aggregate_spend_per_composite from aggregate_benefits, plus a
    // computed "Effective Annual Attachment" preview line. Needs cross-
    // component plumbing (policy + benefit fetch on the endorsement panel);
    // worth a separate story so this PR can ship the BE/FE contract first.
    //
    // `min: 0.01` matches the BE positive-only guard (PR #3552 ErrInvalid-
    // AnnualAttachmentOverride). The validator catches a typo client-side
    // before the BE returns 400.
    {
      source: "annual_attachment_override",
      label: "Annual Attachment (Flat)",
      type: "dollar",
      defaultValue: null,
      required: false,
      min: 0.01,
      helperText:
        "Optional. When set, locks the annual attachment to this dollar value for the full policy year and bypasses the covered-composite × monthly-spend × factor breakdown. FIXED — mid-year enrollment changes do NOT adjust it. Use only when the carrier contract states a flat annual attachment. If the contract is per-composite (enrollment-sensitive), leave blank and use the breakdown inputs instead. Monthly prorated attachment is still derived from this value.",
    },
    {
      source: "accommodation_factor",
      label: "Accommodation Factor",
      type: "percent",
      defaultValue: 1,
      max: null,
      helperText:
        "Factor applied to monthly accommodation calculation (defaults to 100%, no upper cap). Ignored when Annual Attachment (Flat) above is set.",
    },
    { source: "minimum_advance_amount", label: "Minimum Advance Amount", type: "dollar", defaultValue: 0 },
    { source: "reimbursement_pct", label: "Reimbursement Percentage", type: "percent", defaultValue: 0 },
    { source: "is_repayment_required", label: "Repayment Required", type: "boolean", defaultValue: false },
    {
      source: "interest_rate_type",
      label: "Interest Rate Type",
      type: "select",
      defaultValue: "Fixed",
      choices: [
        { id: "Fixed", name: "Fixed" },
        { id: "Variable", name: "Variable" },
      ],
    },
    {
      source: "interest_rate_value",
      label: "Interest Rate",
      type: "percent",
      defaultValue: 0,
      showWhen: "is_repayment_required",
    },
    { source: "no_advances_first_months", label: "No Advances First N Months", type: "number", defaultValue: 0 },
    { source: "is_no_advances_last_month", label: "No Advances Last Month", type: "boolean", defaultValue: false },
    {
      source: "repayment_deadline_days",
      label: "Repayment Deadline (Days)",
      type: "number",
      defaultValue: 90,
      showWhen: "is_repayment_required",
    },
    { source: "is_prorated_minimum_floor", label: "Prorated Minimum Floor", type: "boolean", defaultValue: false },
  ],

  experience_credit: [
    { source: "credit_rate", label: "Credit Rate", type: "percent", defaultValue: 0 },
    { source: "loss_ratio_threshold", label: "Loss Ratio Threshold", type: "percent", defaultValue: 0 },
  ],

  experience_refund: [
    { source: "min_loss_ratio", label: "Minimum Loss Ratio", type: "percent", defaultValue: 0, required: true },
    { source: "max_pct", label: "Maximum Refund Percentage", type: "percent", defaultValue: 0, required: true },
    {
      source: "calculation_method",
      label: "Calculation Method",
      type: "select",
      defaultValue: "sliding_scale",
      choices: [
        { id: "sliding_scale", name: "Sliding Scale" },
        { id: "flat", name: "Flat" },
        { id: "tiered", name: "Tiered" },
      ],
    },
  ],

  rate_caps: [
    { source: "max_percentage", label: "Maximum Percentage", type: "percent", defaultValue: 0 },
    {
      source: "is_applies_to_agg_specific",
      label: "Applies to Aggregate Specific",
      type: "boolean",
      defaultValue: false,
    },
  ],

  no_new_lasers: [
    { source: "is_at_renewal", label: "At Renewal", type: "boolean", defaultValue: false },
    { source: "is_no_revised_lasers", label: "No Revised Lasers", type: "boolean", defaultValue: false },
    { source: "effective_date", label: "Effective Date", type: "date", defaultValue: "" },
  ],

  advance_funding: [
    { source: "funding_percentage", label: "Funding Percentage", type: "percent", defaultValue: 0 },
    { source: "minimum_claim_payment", label: "Minimum Claim Payment", type: "dollar", defaultValue: 0 },
    { source: "subsequent_threshold", label: "Subsequent Threshold", type: "dollar", defaultValue: 0 },
  ],

  admin_changes: [], // Entries managed via junction sub-collection

  leave_of_absence: [
    { source: "coverage_continuation_days", label: "Coverage Continuation (Days)", type: "number", defaultValue: 0 },
    { source: "is_premium_payment_required", label: "Premium Payment Required", type: "boolean", defaultValue: false },
  ],

  amendments: [], // Entries managed via junction sub-collection

  provider_dispute_credit: [
    { source: "credit_amount", label: "Credit Amount", type: "dollar", defaultValue: 0, required: true },
  ],

  plan_mirroring: [{ source: "is_active", label: "Active", type: "boolean", defaultValue: false }],

  retained_corridor: [
    {
      source: "min_retained_pct",
      label: "Minimum Retained Percentage",
      type: "percent",
      defaultValue: 0,
      required: true,
    },
    {
      source: "max_retained_pct",
      label: "Maximum Retained Percentage",
      type: "percent",
      defaultValue: 0,
      required: true,
    },
  ],

  specific_terminal_liability: [
    { source: "months_covered", label: "Months Covered", type: "number", defaultValue: 12, required: true },
    { source: "is_paid_basis", label: "Paid Basis", type: "boolean", defaultValue: false },
    { source: "max_benefit", label: "Maximum Benefit", type: "dollar", defaultValue: 0, required: true },
  ],

  aggregate_terminal_liability: [
    { source: "months_covered", label: "Months Covered", type: "number", defaultValue: 12, required: true },
    { source: "is_incurred_basis", label: "Incurred Basis", type: "boolean", defaultValue: false },
    { source: "max_benefit", label: "Maximum Benefit", type: "dollar", defaultValue: 0, required: true },
  ],

  contingent_terminal_liability: [
    { source: "months_covered", label: "Months Covered", type: "number", defaultValue: 12, required: true },
  ],

  clinical_trials: [
    { source: "per_patient_maximum", label: "Per Patient Maximum", type: "dollar", defaultValue: 0, required: true },
  ],

  transplant_coverage: [
    { source: "separate_deductible", label: "Separate Deductible", type: "dollar", defaultValue: 0, required: true },
    { source: "lifetime_maximum", label: "Lifetime Maximum", type: "dollar", defaultValue: 0, required: true },
    { source: "is_requires_coe", label: "Requires Center of Excellence", type: "boolean", defaultValue: false },
  ],

  travel_medical: [
    { source: "per_incident_limit", label: "Per Incident Limit", type: "dollar", defaultValue: 0, required: true },
    { source: "annual_maximum", label: "Annual Maximum", type: "dollar", defaultValue: 0, required: true },
    { source: "is_emergency_only", label: "Emergency Only", type: "boolean", defaultValue: false },
  ],

  reference_based_pricing: [
    {
      source: "medicare_percentage",
      label: "Medicare Percentage",
      type: "percent",
      defaultValue: 0,
      required: true,
      helperText: "Expressed as percentage of Medicare reimbursement rate",
    },
  ],

  specialty_drug: [
    { source: "separate_deductible", label: "Separate Deductible", type: "dollar", defaultValue: 0, required: true },
  ],

  exclusions: [
    { source: "is_cgt_exclusion", label: "CGT Exclusion", type: "boolean", defaultValue: false },
    { source: "is_late_entrants", label: "Late Entrants", type: "boolean", defaultValue: false },
    { source: "is_retirees", label: "Retirees", type: "boolean", defaultValue: false },
    {
      source: "retiree_ages_covered",
      label: "Retiree Ages Covered",
      type: "text",
      defaultValue: "",
      showWhen: "is_retirees",
    },
  ],

  domestic_reimbursement_adjustments: [], // Entries managed via junction sub-collection
};

// ─────────────────────────────────────────────────────────────
// Junction schema types
//
// Describes the sub-collection / junction-table data that can
// be supplied alongside scalar config at endorsement creation.
// ─────────────────────────────────────────────────────────────

export type JunctionType =
  | "string_array" // EditableCodeChipSection
  | "codes" // Nested { icd: [], cpt: [] }
  | "qualifying_conditions" // Chip toggle picker
  | "claim_ids" // ClaimIdPicker
  | "tiers" // TieredDeductibleTier table
  | "admin_change_entries" // AdminChangeEntry table
  | "amendment_entries" // AmendmentEntry table
  | "domestic_reimbursement_entries"; // DomesticReimbursementEntry table

export interface JunctionSchema {
  /** The config key where this data lives (e.g. "excluded_icd", "tiers") */
  key: string;
  /** Display label for the section header */
  label: string;
  /** Which junction UI component to render */
  junctionType: JunctionType;
  /** For string_array: color key from CODE_CHIP_COLORS */
  chipColor?: string;
  /** For string_array: max character length per value (DB column constraint) */
  maxLength?: number;
  /** For string_array: input placeholder */
  placeholder?: string;
}

type EndorsementJunctionSchemaMap = Partial<Record<EndorsementType, JunctionSchema[]>>;

export const ENDORSEMENT_JUNCTION_SCHEMAS: EndorsementJunctionSchemaMap = {
  deductible_modification: [
    { key: "qualifying_conditions", label: "Qualifying Conditions", junctionType: "qualifying_conditions" },
    { key: "codes", label: "ICD / CPT Codes", junctionType: "codes" },
  ],
  exclusions: [
    {
      key: "excluded_icd",
      label: "Excluded ICD Codes",
      junctionType: "string_array",
      chipColor: "icd",
      maxLength: 8,
      placeholder: "e.g. E11.9",
    },
    {
      key: "excluded_cpt",
      label: "Excluded CPT Codes",
      junctionType: "string_array",
      chipColor: "cpt",
      maxLength: 5,
      placeholder: "e.g. 99213",
    },
  ],
  plan_mirroring: [
    {
      key: "excluded_icd",
      label: "Excluded ICD Codes",
      junctionType: "string_array",
      chipColor: "icd",
      maxLength: 8,
      placeholder: "e.g. E11.9",
    },
    {
      key: "excluded_cpt",
      label: "Excluded CPT Codes",
      junctionType: "string_array",
      chipColor: "cpt",
      maxLength: 5,
      placeholder: "e.g. 99213",
    },
  ],
  clinical_trials: [
    {
      key: "phases",
      label: "Covered Phases",
      junctionType: "string_array",
      chipColor: "phase",
      placeholder: "Enter phase...",
    },
    {
      key: "excluded_icd",
      label: "Excluded ICD Codes",
      junctionType: "string_array",
      chipColor: "icd",
      maxLength: 8,
      placeholder: "e.g. E11.9",
    },
  ],
  transplant_coverage: [
    {
      key: "excluded_cpt",
      label: "Excluded CPT Codes",
      junctionType: "string_array",
      chipColor: "cpt",
      maxLength: 5,
      placeholder: "e.g. 99213",
    },
  ],
  travel_medical: [
    {
      key: "excluded_countries",
      label: "Excluded Countries",
      junctionType: "string_array",
      chipColor: "country",
      maxLength: 2,
      placeholder: "e.g. US, GB",
    },
  ],
  reference_based_pricing: [
    {
      key: "excluded_cpt",
      label: "Excluded CPT Codes",
      junctionType: "string_array",
      chipColor: "cpt",
      maxLength: 5,
      placeholder: "e.g. 99213",
    },
    {
      key: "excluded_facilities",
      label: "Excluded Facilities",
      junctionType: "string_array",
      chipColor: "facility",
      placeholder: "Enter facility...",
    },
  ],
  specialty_drug: [
    {
      key: "ndc_codes",
      label: "NDC Codes",
      junctionType: "string_array",
      chipColor: "ndc",
      placeholder: "Enter NDC code...",
    },
  ],
  contingent_terminal_liability: [
    {
      key: "trigger_types",
      label: "Trigger Types",
      junctionType: "string_array",
      chipColor: "trigger",
      placeholder: "Enter trigger type...",
    },
  ],
  provider_dispute_credit: [{ key: "credited_claim_ids", label: "Credited Claims", junctionType: "claim_ids" }],
  tiered_deductible: [{ key: "tiers", label: "Deductible Tiers", junctionType: "tiers" }],
  admin_changes: [{ key: "admin_change_entries", label: "Change Entries", junctionType: "admin_change_entries" }],
  amendments: [{ key: "amendment_entries", label: "Amendment Entries", junctionType: "amendment_entries" }],
  domestic_reimbursement_adjustments: [
    {
      key: "domestic_reimbursement_entries",
      label: "Reimbursement Entries",
      junctionType: "domestic_reimbursement_entries",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Check if a type has a field schema defined */
export const hasFieldSchema = (type: EndorsementType): boolean => type in ENDORSEMENT_FIELD_SCHEMAS;

/** Get the field schema array for a type (returns undefined if not defined) */
export const getFieldSchema = (type: EndorsementType): FieldSchema[] | undefined => ENDORSEMENT_FIELD_SCHEMAS[type];

/** Get default config values for a type (used in create mode) */
export const getDefaultConfig = (type: EndorsementType): Record<string, unknown> => {
  const schema = ENDORSEMENT_FIELD_SCHEMAS[type];
  if (!schema) return {};
  return Object.fromEntries(schema.map((f) => [f.source, f.defaultValue]));
};

/** Get junction schemas for a type (returns empty array if none) */
export const getJunctionSchema = (type: EndorsementType): JunctionSchema[] => ENDORSEMENT_JUNCTION_SCHEMAS[type] ?? [];

/** Check if a type has any junction data sections */
export const hasJunctionSchema = (type: EndorsementType): boolean =>
  (ENDORSEMENT_JUNCTION_SCHEMAS[type] ?? []).length > 0;
